/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Link } from 'react-router-dom'
import { theme } from '../../styles/theme'
import { Button } from '../UI/Button'

const gettingStartedStyles = css`
  max-width: 800px;
  margin: 0 auto;
  padding: 30px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  margin-bottom: 50px;
  
  h2 {
    color: ${theme.colors.primary};
    margin-bottom: 30px;
    padding-bottom: 15px;
    border-bottom: 2px solid #eee;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 20px 15px;
  }
`

const stepSectionStyles = css`
  margin-bottom: 30px;
  padding: 20px;
  background-color: ${theme.colors.light};
  border-radius: 8px;
  
  h4 {
    color: ${theme.colors.primary};
    margin-bottom: 15px;
    font-weight: 600;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 10px;
  }
  
  ul {
    padding-left: 20px;
    margin-bottom: 15px;
  }
  
  li {
    margin-bottom: 8px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 15px;
    margin-left: -5px;
    margin-right: -5px;
    border-radius: 6px;
  }
`

const scrollMarginStyles = css`
  scroll-margin-top: 90px;
`

export const GettingStartedSection = () => {
  return (
    <div id="getting-started" css={scrollMarginStyles} className="container mb-5">
      <div css={gettingStartedStyles}>
        <h2 className="text-center mb-4">Getting Started</h2>
        
        <div className="getting-started-section">                
          <div className="row mb-3">
            <div className="col-md-6 mb-3">
              <div css={stepSectionStyles} className="h-100">
                <h4><i className="fas fa-book me-2"></i> 1. Read the FAQ</h4>
                <p>Before getting started, familiarize yourself with our service. We have created a comprehensive <Link to="/faq" className="link-primary">FAQ</Link> which answers many questions including:</p>
                <ul>
                  <li>An overview of our offering</li>
                  <li>Limitations of back testing</li>
                  <li>Account requirements and recommendations</li>
                  <li>Comparing and selecting the right strategies</li>
                </ul>
                <p>Also consider using our <Link to="/retirement-planner" className="link-primary">Retirement Planner</Link> to determine your required investment returns and capital needs.</p>                            
              </div>
            </div>
            
            <div className="col-md-6 mb-3">
              <div css={stepSectionStyles} className="h-100">
                <h4><i className="fas fa-search me-2"></i> 2. Choose Your Strategy</h4>
                <p><a href="#" className="link-primary">Browse our selection of strategies</a> and select one based on:</p>
                <ul>
                  <li>Execution metrics including rebalancing frequency and portfolio complexity</li>
                  <li>Backtested performance metrics such as annualized return and maximum drawdown</li>
                  <li>Risk assessment ratios including Sharpe, Sortino, and Calmar</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="row mb-3">                
            <div className="col-md-6 mb-3">
              <div css={stepSectionStyles} className="h-100">
                <h4><i className="fas fa-credit-card me-2"></i> 3. Subscribe</h4>
                <p>Once you have chosen a strategy:</p>
                <ul>
                  <li>Ensure that you have Telegram installed on your device</li>
                  <li>Complete the subscription process by clicking on Subscribe Now</li>
                </ul>
                <p>There is no long-term commitment required. You can cancel your subscription or switch between strategies at any time.</p>
              </div>
            </div>
            
            <div className="col-md-6 mb-3">
              <div css={stepSectionStyles} className="h-100">
                <h4><i className="fab fa-telegram-plane me-2"></i> 4. Join Telegram Channel</h4>
                <p>After subscribing:</p>
                <ul>
                  <li>You will receive a personalized, time-sensitive link to join a private Telegram channel</li>
                  <li>Execute trades in your brokerage account according to the latest published allocations</li>
                  <li>Add our <a href="https://t.me/FintechZL_bot" target="_blank" rel="noopener noreferrer">Telegram Bot</a> to your contacts and send a "/start" message</li>                                
                </ul>
              </div>
            </div>
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6 mb-3">
              <div css={stepSectionStyles} className="h-100">
                <h4><i className="fas fa-bell me-2"></i> 5. Receive Alerts</h4>
                <p>When rebalance events occur:</p>
                <ul>
                  <li>You will receive notifications in your strategy's Telegram channel</li>
                  <li>Alerts include precise allocation percentages for each holding</li>                                
                </ul>
                <p>All strategies rebalance at most once a day, typically 5-10 minutes before market close. Rebalancing frequency varies by strategy and is shown in each strategy's metrics report.</p>
              </div>
            </div>
            
            <div className="col-md-6 mb-3">
              <div css={stepSectionStyles} className="h-100">
                <h4><i className="fas fa-chart-line me-2"></i> 6. Execute Trades</h4>
                <p>Rebalance your portfolio according to the latest allocations. You can place orders at your brokerage:</p>
                <ul>
                  <li>Immediately after receiving the alert (recommended)</li>
                  <li>After hours for execution the following morning</li>
                  <li>At another time of your choosing</li>
                </ul>
                <p>For details on execution timing, please see the relevant section under <Link to="/faq#trading-mechanics">Trading Mechanics</Link>.</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-5">
            <p className="lead mb-4">Begin today by selecting a tactical asset allocation portfolio strategy that aligns with your investment goals.</p>
            <a href="#" className="btn-view-strategies">
              <Button variant="subscribe">View Strategies</Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}